import type { HeroProps } from '@/components/pet/Hero';
import type { PublicationsProps } from '@/components/pet/Publications';
import { sharedConfig } from '@/pageConfig/shared';
import proceduresImg from '@/static/assets/pet/coverage.desktop/procedures.svg';
import ogImage from '@/static/assets/pet/og.png';
import publications from '@/static/assets/home/press_banner_without_inc.svg';
import publicationsSm from '@/static/assets/pet/press_banner_without_inc_small.svg';
import type { BasePageConfig } from '../../../BasePageConfig';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade Pet Insurance',
  description: 'Insurance policies for pet owners',
  image: proceduresImg.src,
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '10',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How does pet insurance work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Since Lemonade Pet works on a reimbursement basis, you can visit your pet in any vet you like in the U.S., as long as they’re licensed to provide veterinary care in the state they operate in. Learn more about <a href=”https://www.lemonade.com/pet/explained/pet-insurance/”>what is pet insurance</a>.',
      },
    },
    {
      '@type': 'Question',
      name: 'What does pet insurance cover',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "At its simplest, Lemonade pet health insurance provides coverage for your pet's treatments and medical care if they get sick or hurt. Click here to learn more about <a href=”https://www.lemonade.com/pet/explained/pet-insurance-coverage/”>what does pet insurance cover</a>?. ",
      },
    },
    {
      '@type': 'Question',
      name: 'How much does pet insurance cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Pet insurance from Lemonade starts at $10 per month. Your premium depends on things like the pet's breed and age, any policy add-ons, choice of deductible and co-insurance, and the cost of vet care in your area. Get the all the insights about <a href=”https://www.lemonade.com/pet/explained/pet-insurance-cost/”>how much does pet insurance cost</a> with our dedicated guide.",
      },
    },
    {
      '@type': 'Question',
      name: 'What are waiting period?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '2 days for accidents, 14 days for illnesses, and 6 months for cruciate ligament events. But if you opted for our preventative package, you can use the benefits in that package the day after you purchased the policy!',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you need my pet’s medical records?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'After you purchase your policy, we’ll ask for your pet’s medical records, which show vital information and help us understand if your pet has previous health issues, aka pre-existing conditions.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are pre-existing conditions and are they covered?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Anything your pet has shown signs of before the policy’s waiting periods are up is considered a pre-existing condition. Currently, we don’t cover pre-existing conditions, but we’ve got your back in case of accidents and any new illnesses, as part of our base coverage.',
      },
    },
    {
      '@type': 'Question',
      name: 'How will I get paid for a claim?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We’ll ask you to enter your bank account information as part of submitting your claim in the Lemonade app. Once it’s approved, we’ll issue payment directly into your account.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Lemonade offer pet insurance near me?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Lemonade’s pet insurance is currently available in 42 states, plus Washington, D.C.— <a href=”https://www.lemonade.com/lemonade-goes-global”> check out where Lemonade Pet (and all our other coverages) are available</a>.',
      },
    },
  ],
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  publications: PublicationsProps | undefined;
}

export const config: Config = {
  seo: {
    title: 'Pet Insurance for Dogs & Cats by Lemonade',
    metaTitle: 'Pet Insurance for Dogs & Cats by Lemonade',
    description:
      'Lemonade Pet Insurance: everything you love about Lemonade now for cats and dogs. Instant claims and a digital experience driven by social good. ❤️',
    ogImage: ogImage.src,
    jsonld: [productJsonld, faqSchema],
  },
  layout: {
    ...sharedConfig['en-US'].layout,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-US'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-US'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-US'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
    menuCheckPricesExtraAnalyticsParams: {
      pet_lp_type: 'default',
      variant: 'default',
    },
  },
  hero: {
    title: 'Pet insurance with\nsuper fast everything',
    subtitle: 'Sign up in seconds. Get paid in minutes. Award-winning service.',
  },
  publications: {
    image: {
      sm: publicationsSm,
      lg: publications,
    },
  },
};
